import { Component, Input, forwardRef, Output, EventEmitter, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { PhoneValidatorService, PhoneValidationResult } from '../../../services/validator/phone-validator.service';

@Component({
  selector: 'app-international-phone-input',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslatePipe, ReactiveFormsModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InternationalPhoneInputComponent),
      multi: true
    }
  ],
  template: `
    <div class="phone-wrapper">
      <mat-form-field appearance="outline" class="phone-form-field">
        <mat-label><span class="label-text">{{ label | translate }}</span>@if (required) {<span class="required-asterisk">*</span>}</mat-label>
        <div matPrefix class="prefix-container">
          <button 
            mat-icon-button 
            type="button"
            [matMenuTriggerFor]="countryMenu"
            [disabled]="disabled"
            class="country-selector">
            <div class="country-button-content">
              <img [src]="selectedCountry?.flagUrl" [alt]="selectedCountry?.code" class="flag-img">
              <mat-icon class="dropdown-arrow">arrow_drop_down</mat-icon>
            </div>
          </button>
          <span class="dial-code">{{ selectedCountry?.dialCode }}</span>
          <div class="separator"></div>
        </div>
        
        <input 
          matInput
          type="tel" 
          [(ngModel)]="phoneNumber"
          (ngModelChange)="onPhoneChange($event)"
          (blur)="onBlur()"
          (keydown)="onKeyDown($event)"
          [disabled]="disabled"
          [placeholder]="'123 456 7890'"
          maxlength="15">
      </mat-form-field>
      
      @if (showValidationError) {
      <div class="external-error-message">
        <mat-icon class="error-icon">error</mat-icon>
        <span>{{ getValidationErrorMessage() | translate }}</span>
      </div>
      }
    </div>
    <mat-menu #countryMenu="matMenu" class="country-menu">
      @for (country of countries; track country.code) {
        <button
          mat-menu-item
          (click)="selectCountry(country)"
          [class.selected]="country.code === selectedCountry?.code">
          <img [src]="country.flagUrl" [alt]="country.code" class="flag-img"> &nbsp;
          <span class="country-name">{{ country.name }}</span>
          <span class="dial-code">{{ country.dialCode }}</span>
        </button>
      }
    </mat-menu>
  `,
  styles: [`
    .phone-wrapper {
      width: 100%;
    }
    
    .phone-form-field {
      width: 100%;
    }
    
    .phone-form-field ::ng-deep .mat-mdc-form-field-wrapper {
      padding-top: 20px;
    }

    .phone-label {
  transform: translateY(-1.5em) scale(0.75);
  transform-origin: left top;
}
    
    .label-text {
      display: inline-block;
    }
    
    .required-asterisk {
      //color: #f44336 !important;
      margin-left: 2px;
      font-weight: bold;
      display: inline;
    }
    
    .phone-form-field ::ng-deep .mat-mdc-form-field-label {
      white-space: nowrap !important;
      overflow: visible !important;
      max-width: none !important;
      width: auto !important;
    }
    
    .phone-form-field ::ng-deep .mat-mdc-floating-label,
    .phone-form-field ::ng-deep .mdc-floating-label {
      white-space: nowrap !important;
      overflow: visible !important;
      max-width: none !important;
      width: auto !important;
      transform-origin: left top !important;
    }
    
    .phone-form-field ::ng-deep .mat-mdc-floating-label.mdc-floating-label--float-above {
      transform: translateY(-190%) scale(0.75) !important;
    }
    
    .phone-form-field ::ng-deep .required-asterisk {
      //color: #f44336 !important;
      margin-left: 2px !important;
      font-weight: bold !important;
    }
    
    .prefix-container {
      display: flex;
      align-items: center;
      gap: 0;
      height: 100%;
    }
    
    .country-selector {
      padding: 0 6px;
      min-width: 48px;
      height: 40px;
      border-radius: 4px;
      transition: background-color 0.2s ease;
    }
    
    .country-selector:hover:not([disabled]) {
      background-color: rgba(0, 0, 0, 0.04);
    }
    
    .country-button-content {
      display: flex;
      align-items: center;
      gap: 4px;
    }
    
    .flag-img {
      width: 20px;
      height: 14px;
      border-radius: 2px;
      object-fit: cover;
      margin-right: 6px;
      vertical-align: middle; 
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
    }
    
    .dropdown-arrow {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: rgba(0, 0, 0, 0.54);
    }
    
    .dial-code {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.87);
      font-size: 13px;
      min-width: 35px;
      text-align: center;
      padding: 0 2px;
    }
    
    .separator {
      width: 1px;
      height: 24px;
      background-color: rgba(0, 0, 0, 0.12);
      margin: 0 6px 0 2px;
    }
    
    @media (max-width: 480px) {
      .country-selector {
        padding: 0 4px;
        min-width: 40px;
      }
      
      .flag-img {
        width: 18px;
        height: 12px;
        margin-right: 4px;
      }
      
      .dial-code {
        font-size: 12px;
        min-width: 30px;
        padding: 0 1px;
      }
      
      .separator {
        margin: 0 4px 0 1px;
      }
      
      .dropdown-arrow {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    }
    
    .country-menu .mat-mdc-menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      min-height: 48px;
      padding: 0 16px;
    }
    
    .country-menu .mat-mdc-menu-item:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
    
    .country-menu .mat-mdc-menu-item.selected {
      background-color: rgba(25, 118, 210, 0.08);
      color: #1976d2;
    }
    
    .country-menu .flag-img {
      width: 20px;
      height: 14px;
      border-radius: 2px;
      object-fit: cover;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }
    
    .country-menu .country-name {
      flex: 1;
      text-align: left;
      font-size: 14px;
    }
    
    .country-menu .dial-code {
      color: rgba(0, 0, 0, 0.6);
      font-weight: 500;
      font-size: 13px;
      min-width: 48px;
      text-align: right;
    }
    
    .country-menu .mat-mdc-menu-item.selected .dial-code {
      color: #1976d2;
    }
    
    .external-error-message {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 12px;
      background-color: #ffebee;
      border: 1px solid #f44336;
      border-radius: 4px;
      margin-top: 4px;
      font-size: 13px;
      color: #d32f2f;
      
      .error-icon {
        color: #f44336;
        font-size: 16px;
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        margin-top: 1px;
      }
      
      span {
        flex: 1;
        line-height: 1.4;
      }
    }
  `]
})
export class InternationalPhoneInputComponent implements ControlValueAccessor, OnInit {

  @Input() label: string = 'field.telefono';
  @Input() disabled: boolean = false;
  @Input() hasError: boolean = false;
  @Input() required: boolean = false;
  @Output() validationChange = new EventEmitter<boolean>();

  showValidationError: boolean = false;
  phoneNumber: string = '';
  countries: any[] = [];
  selectedCountry: any;
  validationResult: PhoneValidationResult = { isValid: true };

  private onChange = (value: string) => { };
  private onTouched = () => { };

  constructor(private phoneValidator: PhoneValidatorService) { }

  ngOnInit() {
    this.countries = this.phoneValidator.getCountryRules();
    this.selectedCountry = this.countries[0];
  }

  selectCountry(country: any) {
    this.selectedCountry = country;
    this.showValidationError = false;
    this.updateValue();
    this.validatePhone();
  }

  onPhoneChange(phone: string) {
    this.phoneNumber = phone;
    this.showValidationError = false;
    this.updateValue();
    this.validatePhone();
  }

  onBlur() {
    if (!this.validatePhone()) {
      this.showValidationError = this.shouldShowValidationError();
    }
    this.onTouched();
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    const isNumber = /^[0-9]$/.test(event.key);

    if (!isNumber && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  validatePhone(): boolean {
    if (!this.selectedCountry) {
      return false;
    }

    if (!this.phoneNumber || this.phoneNumber.trim() === '') {
      const isValid = !this.required;
      this.validationResult = { isValid };
      this.validationChange.emit(isValid);
      return isValid;
    }

    this.validationResult = this.phoneValidator.validatePhoneNumber(this.phoneNumber, this.selectedCountry.code);
    this.validationChange.emit(this.validationResult.isValid);
    return this.validationResult.isValid;
  }

  getValidationErrorMessage(): string {
    if ((!this.phoneNumber || this.phoneNumber.trim() === '') && this.required) {
      return 'error.telefono.required';
    }
    return 'field.telefono.invalid';
  }

  private shouldShowValidationError(): boolean {
    if (!this.phoneNumber || this.phoneNumber.trim() === '') {
      return this.required;
    }
    return !this.validationResult.isValid;
  }

  isPhoneValid(): boolean {
    return this.validatePhone();
  }

  private updateValue() {
    const fullNumber = this.phoneNumber ? `${this.selectedCountry.dialCode}${this.phoneNumber}` : '';
    this.onChange(fullNumber);
    this.onTouched();
  }

  writeValue(value: string): void {
    if (value) {
      const country = this.countries.find(c => value.startsWith(c.dialCode));
      if (country) {
        this.selectedCountry = country;
        this.phoneNumber = value.substring(country.dialCode.length);
      } else {
        this.phoneNumber = value;
      }
    } else {
      this.phoneNumber = '';
    }
    
    // Valida immediatamente dopo aver impostato il valore
    setTimeout(() => this.validatePhone(), 0);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}